import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';

const BookingPrintThermal = ({ booking, customer }) => {
  const [termsAndConditions, setTermsAndConditions] = useState([]);

  useEffect(() => {
    loadTermsAndConditions();
  }, []);

  const loadTermsAndConditions = async () => {
    try {
      // Fetch only terms and conditions for bookings module
      const res = await axiosInstance.get('/terms-conditions/type/bookings');
      const activeTerms = (res.data || []).filter(t => t.is_active !== false);
      setTermsAndConditions(activeTerms);
    } catch (err) {
      console.error('Error loading T&C:', err);
      setTermsAndConditions([]);
    }
  };

  // Calculate totals
  const calculateTotals = () => {
    if (!booking || !booking.items) {
      return { subtotal: 0, totalAdvance: 0, metalCredit: 0 };
    }

    const totalWeight = booking.items.reduce((sum, item) => sum + (parseFloat(item.weight) || 0), 0);
    
    let subtotal = 0;
    if (booking.base24k_rate_ex_gst && totalWeight > 0) {
      subtotal = (totalWeight * booking.base24k_rate_ex_gst) / 10;
    }

    const advancePaid = (booking.advance_payments || []).reduce((sum, p) => {
      if (p.mode === 'Metal Credit') return sum;
      const amount = parseFloat(p.amount) || 0;
      return p.transaction_type === 'REFUND' ? sum - amount : sum + amount;
    }, 0);

    const metalAdvance = (booking.advance_payments || []).reduce((sum, p) => {
      if (p.mode === 'Metal Credit' && p.advance_equivalent) {
        return sum + parseFloat(p.advance_equivalent);
      }
      return sum;
    }, 0);

    const metalCredit = (booking.advance_payments || []).reduce((sum, p) => {
      if (p.mode === 'Metal Credit') {
        // Check if payment has deposits array with details
        if (p.deposits && Array.isArray(p.deposits) && p.deposits.length > 0) {
          return sum + p.deposits.reduce((depSum, dep) => depSum + parseFloat(dep.fine_wt || 0), 0);
        }
        // Fallback to old structure
        if (p.fine_wt) {
          return sum + parseFloat(p.fine_wt);
        }
      }
      // Old Gold should NOT be included in Metal Credit total
      return sum;
    }, 0);

    const totalAdvance = advancePaid + metalAdvance;

    return { subtotal, totalAdvance, metalCredit };
  };

  const totals = calculateTotals();

  // Group payments by mode
  const groupPaymentsByMode = () => {
    const grouped = {};
    (booking.advance_payments || []).forEach(payment => {
      const mode = payment.mode || 'Unknown';
      if (!grouped[mode]) {
        grouped[mode] = [];
      }
      grouped[mode].push(payment);
    });
    return grouped;
  };

  const paymentsByMode = groupPaymentsByMode();

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Format amount in Indian comma format
  const formatIndianCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return num.toLocaleString('en-IN', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  // Convert number to words
  const numberToWords = (num) => {
    if (!num || num === 0) return 'Zero Rupees';
    
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    
    const convertLessThanThousand = (n) => {
      if (n === 0) return '';
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
      return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + convertLessThanThousand(n % 100) : '');
    };
    
    const absNum = Math.abs(Math.floor(num));
    let result = '';
    
    if (absNum >= 10000000) {
      const crores = Math.floor(absNum / 10000000);
      result += convertLessThanThousand(crores) + ' Crore ';
      num = absNum % 10000000;
    } else {
      num = absNum;
    }
    
    if (num >= 100000) {
      const lakhs = Math.floor(num / 100000);
      result += convertLessThanThousand(lakhs) + ' Lakh ';
      num = num % 100000;
    }
    
    if (num >= 1000) {
      const thousands = Math.floor(num / 1000);
      result += convertLessThanThousand(thousands) + ' Thousand ';
      num = num % 1000;
    }
    
    if (num > 0) {
      result += convertLessThanThousand(num);
    }
    
    return result.trim() + ' Rupees Only';
  };

  const calculateModeSubtotal = (payments) => {
    return payments.reduce((sum, p) => {
      const amount = parseFloat(p.amount) || 0;
      return p.transaction_type === 'REFUND' ? sum - amount : sum + amount;
    }, 0);
  };

  if (!booking) return null;

  return (
    <div id="booking-thermal-print-content" className="print-content-hidden">
      {booking?.has_provisional_changes && <div className="provisional-watermark">PROVISIONAL</div>}
      <div style={{
        width: '80mm',
        padding: '2mm',
        fontFamily: "'Courier New', monospace",
        fontSize: '16px',
        lineHeight: '1.15',
        color: '#000',
        wordWrap: 'break-word'
      }}>
        
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2px', borderBottom: '2px solid #000', paddingBottom: '2px' }}>
          <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '2px' }}>CUSTOMER BOOKING</div>
          <div style={{ fontSize: '13px', fontWeight: 'bold' }}>Booking ID: {booking.booking_id}</div>
          <div style={{ fontSize: '16px' }}>Date: {formatDate(booking.booking_date || booking.created_at)}</div>
        </div>

        {/* Customer Information */}
        <div style={{ marginBottom: '2px', borderBottom: '1px solid #000', paddingBottom: '2px' }}>
          <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '1px' }}>CUSTOMER INFORMATION</div>
          {customer && (
            <>
              <div style={{ fontSize: '16px', marginBottom: '2px' }}>
                <strong>Name:</strong> {customer.name}
              </div>
              {customer.mobile && (
                <div style={{ fontSize: '16px', marginBottom: '2px' }}>
                  <strong>Mobile:</strong> {customer.mobile}
                </div>
              )}
              {customer.alternate_mobile && (
                <div style={{ fontSize: '16px', marginBottom: '2px' }}>
                  <strong>Alt Mobile:</strong> {customer.alternate_mobile}
                </div>
              )}
              {customer.email && (
                <div style={{ fontSize: '16px', marginBottom: '2px', wordBreak: 'break-all' }}>
                  <strong>Email:</strong> {customer.email}
                </div>
              )}
              {customer.address && (
                <div style={{ fontSize: '16px', marginBottom: '2px', wordWrap: 'break-word' }}>
                  <strong>Address:</strong> {customer.address}
                </div>
              )}
            </>
          )}
        </div>

        {/* Rate Information */}
        {booking.base24k_rate_ex_gst && (
          <div style={{ marginBottom: '2px', borderBottom: '1px solid #000', paddingBottom: '2px', backgroundColor: '#fffef0' }}>
            <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '1px' }}>RATE INFORMATION</div>
            <div style={{ fontSize: '16px', marginBottom: '2px' }}>
              <strong>24K Base Rate:</strong> ₹{formatIndianCurrency(booking.base24k_rate_ex_gst)}/g
            </div>
            {booking.lock_date && (
              <div style={{ fontSize: '16px', marginBottom: '2px' }}>
                <strong>Lock Date:</strong> {formatDate(booking.lock_date)}
              </div>
            )}
            {booking.gst_applicable === false ? (
              <div style={{ fontSize: '16px', marginBottom: '2px' }}>
                <strong>GST:</strong> Kaccha (No GST)
              </div>
            ) : (
              <div style={{ fontSize: '16px', marginBottom: '2px' }}>
                <strong>GST:</strong> {booking.gst_percent_snapshot || 3}%
              </div>
            )}
          </div>
        )}

        {/* Booking Items */}
        <div style={{ marginBottom: '2px' }}>
          <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '2px', borderBottom: '1px solid #000', paddingBottom: '2px' }}>
            BOOKING ITEMS ({booking.items?.length || 0})
          </div>
          
          {booking.items?.map((item, idx) => (
            <div key={idx} style={{ marginBottom: '3px', border: '1px solid #000', padding: '2px' }}>
              {/* Item Title */}
              <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '2px', wordWrap: 'break-word' }}>
                {idx + 1}. {item.booking_type}
              </div>

              {/* Item Details */}
              <div style={{ fontSize: '9px', lineHeight: '1.5' }}>
                <div>
                  <strong>Weight:</strong> {item.weight}g
                </div>
                {item.public_remark && (
                  <div style={{ marginTop: '4px', wordWrap: 'break-word' }}>
                    <strong>Remark:</strong> {item.public_remark}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Payments & Deposits */}
        {booking.advance_payments && booking.advance_payments.length > 0 && (
          <div style={{ marginBottom: '2px', borderTop: '2px solid #000', paddingTop: '2px' }}>
            <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '2px' }}>
              💳 PAYMENTS & DEPOSITS
            </div>
            
            {Object.keys(paymentsByMode).map((mode, modeIdx) => (
              <div key={modeIdx} style={{ marginBottom: '2px' }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '1px', textTransform: 'uppercase' }}>
                  {mode}
                </div>
                
                {/* Special handling for Old Gold */}
                {mode === 'Old Gold' ? (
                  <>
                    {paymentsByMode[mode].map((payment, payIdx) => (
                      <div key={payIdx} style={{ fontSize: '9px', marginBottom: '2px', border: '1px solid #000', padding: '3px' }}>
                        <div style={{ marginBottom: '1px' }}>
                          <strong>Date:</strong> {formatDate(payment.date)}
                        </div>
                        <div style={{ marginBottom: '1px' }}>
                          <strong>Amount:</strong> ₹{formatIndianCurrency(payment.amount || 0)}
                        </div>
                        
                        {/* Old Gold Items Breakdown */}
                        {payment.items && payment.items.length > 0 && (
                          <div style={{ marginTop: '4px', borderTop: '1px dashed #000', paddingTop: '3px' }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>Item Details:</div>
                            {payment.items.map((item, itemIdx) => (
                              <div key={itemIdx} style={{ marginLeft: '4px', marginBottom: '1px', paddingLeft: '4px', borderLeft: '2px solid #000' }}>
                                {item.item_name && <div><strong>Item:</strong> {item.item_name}</div>}
                                {item.gross_wt && <div><strong>Gross Wt:</strong> {item.gross_wt}g</div>}
                                {item.less_wt && <div><strong>Less Wt:</strong> {item.less_wt}g</div>}
                                {item.net_wt && <div><strong>Net Wt:</strong> {item.net_wt}g</div>}
                                {item.purity && <div><strong>Purity:</strong> {item.purity}%</div>}
                                {item.fine_wt && <div><strong>Fine Wt:</strong> {item.fine_wt}g</div>}
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {payment.note && (
                          <div style={{ marginTop: '3px', wordWrap: 'break-word' }}>
                            <strong>Note:</strong> {payment.note}
                          </div>
                        )}
                      </div>
                    ))}
                    <div style={{ fontSize: '9px', fontWeight: 'bold', textAlign: 'right', marginBottom: '2px', paddingTop: '2px', borderTop: '1px solid #000' }}>
                      Old Gold Total: ₹{formatIndianCurrency(calculateModeSubtotal(paymentsByMode[mode]))}
                    </div>
                  </>
                ) : mode === 'Metal Credit' ? (
                  <>
                    {paymentsByMode[mode]
                      .filter(p => parseFloat(p.fine_wt || p.fine_grams || 0) > 0 || (p.deposits && p.deposits.length > 0))
                      .map((payment, payIdx) => {
                        // Check if payment has deposits array with details
                        const hasDeposits = payment.deposits && Array.isArray(payment.deposits) && payment.deposits.length > 0;
                        
                        return (
                          <div key={payIdx} style={{ fontSize: '9px', marginBottom: '2px', border: '2px solid #000', padding: '2px' }}>
                            {hasDeposits ? (
                              // Show each deposit with complete details
                              <>
                                {payment.deposits.map((deposit, depIdx) => (
                                  <div key={depIdx} style={{ marginBottom: '2px', paddingBottom: '2px', borderBottom: depIdx < payment.deposits.length - 1 ? '1px dashed #999' : 'none' }}>
                                    <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '2px', color: '#000' }}>
                                      Deposit {depIdx + 1}
                                      {deposit.item_name && `: ${deposit.item_name}`}
                                    </div>
                                    
                                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1px' }}>
                                      <tbody>
                                        <tr>
                                          <td style={{ padding: '2px', borderBottom: '1px solid #ddd' }}><strong>Date</strong></td>
                                          <td style={{ padding: '2px', borderBottom: '1px solid #ddd' }}>{formatDate(deposit.date || payment.date)}</td>
                                        </tr>
                                        {(deposit.type || deposit.type_name_other) && (
                                          <tr>
                                            <td style={{ padding: '2px', borderBottom: '1px solid #ddd' }}><strong>Type</strong></td>
                                            <td style={{ padding: '2px', borderBottom: '1px solid #ddd' }}>{deposit.type_name_other || deposit.type}</td>
                                          </tr>
                                        )}
                                        {deposit.gross_wt && (
                                          <tr>
                                            <td style={{ padding: '2px', borderBottom: '1px solid #ddd' }}><strong>Gross Wt</strong></td>
                                            <td style={{ padding: '2px', borderBottom: '1px solid #ddd' }}>{deposit.gross_wt}g</td>
                                          </tr>
                                        )}
                                        {deposit.less_wt && (
                                          <tr>
                                            <td style={{ padding: '2px', borderBottom: '1px solid #ddd' }}><strong>Less Wt</strong></td>
                                            <td style={{ padding: '2px', borderBottom: '1px solid #ddd' }}>{deposit.less_wt}g</td>
                                          </tr>
                                        )}
                                        {deposit.net_wt && (
                                          <tr>
                                            <td style={{ padding: '2px', borderBottom: '1px solid #ddd' }}><strong>Net Wt</strong></td>
                                            <td style={{ padding: '2px', borderBottom: '1px solid #ddd' }}>{deposit.net_wt}g</td>
                                          </tr>
                                        )}
                                        {(deposit.purity || deposit.purity_label) && (
                                          <tr>
                                            <td style={{ padding: '2px', borderBottom: '1px solid #ddd' }}><strong>Purity</strong></td>
                                            <td style={{ padding: '2px', borderBottom: '1px solid #ddd' }}>
                                              {deposit.purity_label || `${deposit.purity}%`}
                                            </td>
                                          </tr>
                                        )}
                                        <tr>
                                          <td style={{ padding: '2px', fontWeight: 'bold' }}><strong>Fine Wt</strong></td>
                                          <td style={{ padding: '2px', fontWeight: 'bold' }}>{deposit.fine_wt || 0}g</td>
                                        </tr>
                                      </tbody>
                                    </table>
                                    
                                    {deposit.reference && (
                                      <div style={{ marginTop: '2px', wordWrap: 'break-word', fontSize: '8px' }}>
                                        <strong>Ref:</strong> {deposit.reference}
                                      </div>
                                    )}
                                    
                                    {deposit.photos && deposit.photos.length > 0 && (
                                      <div style={{ marginTop: '3px' }}>
                                        <div style={{ fontSize: '8px', fontWeight: 'bold', marginBottom: '2px' }}>Photos: {deposit.photos.length} attached</div>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '2px' }}>
                                          {deposit.photos.slice(0, 2).map((photo, photoIdx) => {
                                            const photoUrl = typeof photo === 'string' ? photo : (photo.edited || photo.original);
                                            return photoUrl ? (
                                              <img 
                                                key={photoIdx}
                                                src={photoUrl} 
                                                alt={`Photo ${photoIdx + 1}`}
                                                crossOrigin="anonymous"
                                                style={{ 
                                                  width: '100%', 
                                                  height: '40px', 
                                                  objectFit: 'cover', 
                                                  border: '1px solid #999',
                                                  display: 'block'
                                                }}
                                                onError={(e) => { e.target.style.display = 'none'; }}
                                              />
                                            ) : null;
                                          })}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </>
                            ) : (
                              // Simple Metal Credit (no deposits array)
                              <>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                  <tbody>
                                    <tr>
                                      <td style={{ padding: '2px', borderBottom: '1px solid #ddd' }}><strong>Date</strong></td>
                                      <td style={{ padding: '2px', borderBottom: '1px solid #ddd' }}>{formatDate(payment.date)}</td>
                                    </tr>
                                    {payment.metal_type && (
                                      <tr>
                                        <td style={{ padding: '2px', borderBottom: '1px solid #ddd' }}><strong>Type</strong></td>
                                        <td style={{ padding: '2px', borderBottom: '1px solid #ddd' }}>{payment.metal_type}</td>
                                      </tr>
                                    )}
                                    {payment.gross_wt && (
                                      <tr>
                                        <td style={{ padding: '2px', borderBottom: '1px solid #ddd' }}><strong>Gross Wt</strong></td>
                                        <td style={{ padding: '2px', borderBottom: '1px solid #ddd' }}>{payment.gross_wt}g</td>
                                      </tr>
                                    )}
                                    {payment.net_wt && (
                                      <tr>
                                        <td style={{ padding: '2px', borderBottom: '1px solid #ddd' }}><strong>Net Wt</strong></td>
                                        <td style={{ padding: '2px', borderBottom: '1px solid #ddd' }}>{payment.net_wt}g</td>
                                      </tr>
                                    )}
                                    {payment.purity && (
                                      <tr>
                                        <td style={{ padding: '2px', borderBottom: '1px solid #ddd' }}><strong>Purity</strong></td>
                                        <td style={{ padding: '2px', borderBottom: '1px solid #ddd' }}>{payment.purity}%</td>
                                      </tr>
                                    )}
                                    <tr>
                                      <td style={{ padding: '2px', fontWeight: 'bold' }}><strong>Fine Wt</strong></td>
                                      <td style={{ padding: '2px', fontWeight: 'bold' }}>{payment.fine_wt || payment.fine_grams || 0}g</td>
                                    </tr>
                                  </tbody>
                                </table>
                                {payment.reference && (
                                  <div style={{ marginTop: '3px', wordWrap: 'break-word' }}>
                                    <strong>Ref:</strong> {payment.reference}
                                  </div>
                                )}
                                {payment.note && (
                                  <div style={{ marginTop: '2px', wordWrap: 'break-word' }}>
                                    <strong>Note:</strong> {payment.note}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })}
                    {paymentsByMode[mode].length > 0 && (
                      <div style={{ fontSize: '9px', fontWeight: 'bold', textAlign: 'right', marginBottom: '2px', paddingTop: '2px', borderTop: '2px solid #000' }}>
                        Total Fine Gold: {paymentsByMode[mode].reduce((sum, p) => {
                          if (p.deposits && p.deposits.length > 0) {
                            return sum + p.deposits.reduce((depSum, dep) => depSum + parseFloat(dep.fine_wt || 0), 0);
                          }
                          return sum + parseFloat(p.fine_wt || p.fine_grams || 0);
                        }, 0).toFixed(3)}g
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div style={{ border: '1px solid #000', marginBottom: '2px' }}>
                      <table style={{ width: '100%', fontSize: '9px', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #000' }}>
                            <th style={{ padding: '2px', textAlign: 'left', borderRight: '1px solid #ddd' }}>Date</th>
                            <th style={{ padding: '2px', textAlign: 'right', borderRight: '1px solid #ddd' }}>Amount</th>
                            <th style={{ padding: '2px', textAlign: 'left' }}>Details</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paymentsByMode[mode].map((payment, payIdx) => {
                            const amount = parseFloat(payment.amount) || 0;
                            const isRefund = payment.transaction_type === 'REFUND';
                            const displayAmount = isRefund ? -amount : amount;
                            
                            return (
                              <tr key={payIdx} style={{ borderBottom: payIdx < paymentsByMode[mode].length - 1 ? '1px solid #ddd' : 'none' }}>
                                <td style={{ padding: '2px', borderRight: '1px solid #ddd', fontSize: '8px' }}>
                                  {formatDate(payment.date)}
                                </td>
                                <td style={{ padding: '2px', textAlign: 'right', borderRight: '1px solid #ddd', color: isRefund ? '#d00' : '#000' }}>
                                  {isRefund ? '-' : ''}₹{formatIndianCurrency(Math.abs(displayAmount))}
                                </td>
                                <td style={{ padding: '2px', fontSize: '8px', wordWrap: 'break-word' }}>
                                  {isRefund && <div style={{ color: '#d00', fontWeight: 'bold' }}>REFUND</div>}
                                  {payment.mode === 'UPI' && payment.upi_ref && <div>Ref: {payment.upi_ref}</div>}
                                  {payment.mode === 'Card' && payment.card_last4 && <div>****{payment.card_last4}</div>}
                                  {(payment.mode === 'IMPS' || payment.mode === 'NEFT/RTGS') && payment.utr_ref && <div>UTR: {payment.utr_ref}</div>}
                                  {payment.mode === 'Cheque' && payment.cheque_no && <div>#{payment.cheque_no}</div>}
                                  {payment.note && <div style={{ fontSize: '8px', color: '#666' }}>{payment.note}</div>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ fontSize: '9px', fontWeight: 'bold', textAlign: 'right', marginBottom: '2px', paddingTop: '2px', borderTop: '1px solid #000' }}>
                      {mode} Total: ₹{formatIndianCurrency(calculateModeSubtotal(paymentsByMode[mode]))}
                    </div>
                  </>
                )}
              </div>
            ))}
            
            {/* Net Advance Total */}
            <div style={{ marginTop: '2px', border: '3px double #000' }}>
              <div style={{ padding: '2px', textAlign: 'center', backgroundColor: '#f9f9f9', borderBottom: totals.metalCredit > 0 ? '2px solid #000' : 'none' }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '2px' }}>NET ADVANCE PAID</div>
                <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '2px' }}>₹{formatIndianCurrency(totals.totalAdvance)}</div>
                <div style={{ fontSize: '8px', fontStyle: 'italic', wordWrap: 'break-word' }}>({numberToWords(totals.totalAdvance)})</div>
              </div>
              
              {totals.metalCredit > 0 && (
                <div style={{ padding: '2px', textAlign: 'center', backgroundColor: '#fff9e6' }}>
                  <div style={{ fontSize: '13px', fontWeight: 'bold' }}>METAL CREDIT</div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{totals.metalCredit.toFixed(2)}g fine gold</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Signatures */}
        <div style={{ marginBottom: '2px', borderTop: '2px solid #000', paddingTop: '2px' }}>
          <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '2px' }}>✍️ SIGNATURES</div>
          
          <div style={{ marginBottom: '2px' }}>
            {booking.management_signature && (
              <>
                <div style={{ fontSize: '9px', fontWeight: 'bold', marginBottom: '2px' }}>Management</div>
                <div style={{ border: '1px solid #000', padding: '2px', marginBottom: '2px' }}>
                  <img src={booking.management_signature} alt="Management Signature" style={{ width: '60mm', height: 'auto', maxHeight: '20mm', display: 'block' }} />
                </div>
              </>
            )}
          </div>

          <div>
            {booking.customer_signature && (
              <>
                <div style={{ fontSize: '9px', fontWeight: 'bold', marginBottom: '2px' }}>Customer</div>
                <div style={{ border: '1px solid #000', padding: '2px', marginBottom: '2px' }}>
                  <img src={booking.customer_signature} alt="Customer Signature" style={{ width: '60mm', height: 'auto', maxHeight: '20mm', display: 'block' }} />
                </div>
                {customer && customer.name && (
                  <div style={{ fontSize: '8px' }}>Name: {customer.name}</div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Terms & Conditions */}
        <div style={{ marginBottom: '2px', borderTop: '2px solid #000', paddingTop: '2px' }}>
          <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '2px' }}>TERMS & CONDITIONS</div>
          {termsAndConditions.length > 0 ? (
            <div style={{ fontSize: '8px', lineHeight: '1.6' }}>
              {termsAndConditions.map((term, idx) => (
                <div key={idx} style={{ marginBottom: '1px', wordWrap: 'break-word' }}>• {term.text || term.content || term}</div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: '8px', fontStyle: 'italic', color: '#666' }}>No terms and conditions configured.</div>
          )}
        </div>

        {/* Footer Spacing */}
        <div style={{ height: '10mm' }}></div>

      </div>
    </div>
  );
};

export default BookingPrintThermal;